import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Comment as CommentIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { EntityType, StatusTransition, MitigationStatus } from 'shared';
import { historyService } from '../../services/history.service';

interface TransitionHistoryProps {
  entityType: EntityType;
  entityId: string;
  maxVisible?: number;
}

// Color mappings for different MUI components
const TIMELINE_DOT_COLORS: Record<
  string,
  'grey' | 'primary' | 'info' | 'warning' | 'success'
> = {
  [MitigationStatus.PLANNED]: 'grey',
  [MitigationStatus.IN_PROGRESS]: 'info',
  [MitigationStatus.IMPLEMENTED]: 'warning',
  [MitigationStatus.VERIFIED]: 'success',
};

const CHIP_COLORS: Record<
  string,
  'default' | 'primary' | 'info' | 'warning' | 'success'
> = {
  [MitigationStatus.PLANNED]: 'default',
  [MitigationStatus.IN_PROGRESS]: 'info',
  [MitigationStatus.IMPLEMENTED]: 'warning',
  [MitigationStatus.VERIFIED]: 'success',
};

export const TransitionHistory: React.FC<TransitionHistoryProps> = ({
  entityType,
  entityId,
  maxVisible = 3,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [transitions, setTransitions] = useState<StatusTransition[]>([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const loadTransitions = async () => {
      setLoading(true);
      try {
        const response = await historyService.getTransitions(
          entityType,
          entityId
        );
        setTransitions(response.transitions);
      } catch (error) {
        console.error('Failed to load transitions:', error);
      } finally {
        setLoading(false);
      }
    };

    if (entityId) {
      loadTransitions();
    }
  }, [entityType, entityId]);

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString();
  };

  const getStatusLabel = (status: string): string => {
    return t(`mitigation.status.${status}`, status);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (transitions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        {t('history.transitions.noTransitions')}
      </Typography>
    );
  }

  const visibleTransitions = expanded
    ? transitions
    : transitions.slice(0, maxVisible);
  const hasMore = transitions.length > maxVisible;

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="subtitle2">
          {t('history.transitions.title')}
        </Typography>
        {hasMore && (
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        )}
      </Box>

      <Timeline
        sx={{
          p: 0,
          m: 0,
          '& .MuiTimelineItem-root:before': {
            flex: 0,
            p: 0,
          },
        }}
      >
        {visibleTransitions.map((transition, index) => (
          <TimelineItem key={transition.id}>
            <TimelineOppositeContent
              sx={{ flex: 0.3, minWidth: 100, py: 1 }}
              variant="caption"
              color="text.secondary"
            >
              {formatDate(transition.createdAt)}
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot
                color={TIMELINE_DOT_COLORS[transition.toStatus] || 'grey'}
                variant={index === 0 ? 'filled' : 'outlined'}
              />
              {index < visibleTransitions.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: 1 }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  label={getStatusLabel(transition.fromStatus)}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
                <Typography variant="caption" color="text.secondary">
                  →
                </Typography>
                <Chip
                  label={getStatusLabel(transition.toStatus)}
                  size="small"
                  color={CHIP_COLORS[transition.toStatus] || 'default'}
                  sx={{ fontSize: '0.7rem' }}
                />
              </Box>
              {transition.comment && (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 1,
                    p: 1,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <CommentIcon
                    fontSize="small"
                    color="action"
                    sx={{ mt: 0.25 }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {transition.comment}
                    </Typography>
                    {transition.transitionByUserName && (
                      <Typography variant="caption" color="text.secondary">
                        — {transition.transitionByUserName}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              )}
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      {hasMore && !expanded && (
        <Box textAlign="center">
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={() => setExpanded(true)}
          >
            {t('common.showMore', { count: transitions.length - maxVisible })}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TransitionHistory;
